/* ═══════════════════════════════════════════════════════════════════════
   EP SHOP — comportements de marque

   Un seul comportement pour l'instant, et il compte : la vidéo du héros.

   L'affiche est l'élément que Google mesure (LCP). Si la vidéo se charge
   en même temps, elle lui vole la bande passante et le score s'effondre
   sans que le rendu ait l'air différent. On attend donc le premier rendu,
   puis l'entrée dans le viewport, avant d'attacher la source.

   Rien ne se charge du tout si l'appareil demande moins d'animation ou
   annonce une connexion économe : l'affiche seule fait le travail.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  function initHeroVideo(root) {
    var stages = (root || document).querySelectorAll('[data-ep-hero-video]');
    if (!stages.length) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var conn = navigator.connection || {};
    var thrifty = conn.saveData === true || /(^|-)2g$/.test(conn.effectiveType || '');
    if (reduced || thrifty) return;

    stages.forEach(function (stage) {
      if (stage.dataset.epHeroBound) return;
      stage.dataset.epHeroBound = '1';

      var video = stage.querySelector('video');
      if (!video) return;

      var start = function () {
        var play = video.play();
        if (play && play.catch) {
          // Lecture refusée (onglet en arrière-plan, économiseur) :
          // l'affiche reste, ce n'est pas une erreur à signaler.
          play.catch(function () {});
        }
        video.addEventListener(
          'playing',
          function () {
            stage.setAttribute('data-ep-playing', '');
          },
          { once: true }
        );
      };

      var arm = function () {
        if (!('IntersectionObserver' in window)) return start();
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                io.disconnect();
                start();
              }
            });
          },
          { rootMargin: '200px' }
        );
        io.observe(stage);
      };

      // Après le premier rendu, pas avant.
      if ('requestIdleCallback' in window) {
        requestIdleCallback(arm, { timeout: 2000 });
      } else {
        setTimeout(arm, 400);
      }
    });
  }

  /* ── Ponctuation française ───────────────────────────────────────────
     Espace insécable avant : ; ! ? et à l'intérieur des guillemets, comme
     le veut la typographie française. Sans ça, « la citation ouvre ici
     et le guillemet fermant se retrouve seul sur la ligne suivante » —
     c'est exactement ce qui arrivait à la bande citation.

     Appliqué aux seuls nœuds de texte, jamais au balisage, et seulement
     quand la page est en français : en anglais ces espaces sont fautives.
     ─────────────────────────────────────────────────────────────────── */
  function initFrenchTypography(root) {
    if ((document.documentElement.lang || '').slice(0, 2) !== 'fr') return;

    var SKIP = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, CODE: 1, PRE: 1, INPUT: 1 };
    var NBSP = '\u00A0';

    function fix(t) {
      return t
        .replace(/«\s+/g, '\u00AB' + NBSP)
        .replace(/\s+»/g, NBSP + '\u00BB')
        .replace(/ +([:;!?])(?=\s|$)/g, NBSP + '$1')
        .replace(/(\d) +(\$|%)/g, '$1' + NBSP + '$2');
    }

    var walker = document.createTreeWalker(root || document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.parentNode || SKIP[node.parentNode.nodeName]) return NodeFilter.FILTER_REJECT;
        return node.nodeValue && node.nodeValue.trim()
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      }
    });

    var node, changed;
    while ((node = walker.nextNode())) {
      changed = fix(node.nodeValue);
      if (changed !== node.nodeValue) node.nodeValue = changed;
    }
  }

  /* ── Onglets de l'explorateur de verres ──────────────────────────────
     Motif ARIA tablist complet : un seul onglet dans l'ordre de
     tabulation, les flèches circulent entre eux, Origine et Fin vont aux
     extrémités.

     Le pliage n'est activé qu'ici, par [data-ep-tabs-ready]. Sans
     JavaScript, les panneaux restent tous visibles à la suite : le
     contenu ne disparaît jamais. Masquer d'abord puis révéler au chargement
     ferait l'inverse — et laisserait la page vide si le script échoue.
     ─────────────────────────────────────────────────────────────────── */
  /* Les onglets sont fabriqués ici, pas en Liquid.

     `{% for b in block.blocks %}` ne voit rien : pour des blocs de thème,
     seuls `content_for 'blocks'` les rend, et il ne les expose pas à la
     boucle. Mesuré en direct : 3 panneaux, 0 onglets. Le tablist arrive
     donc vide et on le remplit à partir des panneaux — ce qui garde la
     promesse d'origine, un onglet par panneau, ni plus ni moins.

     Tout se lit dans le panneau : le code dans `.ep-lt__h`, la condition
     de lumière dans `.ep-lt__cond`, le pictogramme dans l'attribut
     `data-ep-tab-icon` (c'est un réglage du panneau qui s'affiche dans
     l'onglet, il n'a pas d'autre chemin). Les identifiants suivent la
     convention déjà posée par le Liquid : `panel-xxx` ↔ `tab-xxx`, donc
     `aria-labelledby` du panneau continue de pointer sur le bon onglet. */
  function construireOnglets(group) {
    var liste = group.querySelector('[role="tablist"]');
    if (!liste || liste.querySelector('[role="tab"]')) return;

    var panneaux = group.querySelectorAll('[role="tabpanel"]');
    if (panneaux.length < 2) return;

    Array.prototype.forEach.call(panneaux, function (panneau, i) {
      var id = panneau.id;
      if (!id) return;

      var bouton = document.createElement('button');
      bouton.type = 'button';
      bouton.className = 'ep-lt__tab';
      bouton.setAttribute('role', 'tab');
      bouton.id = id.indexOf('panel-') === 0 ? 'tab-' + id.slice(6) : 'tab-' + id;
      bouton.setAttribute('aria-controls', id);
      bouton.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      bouton.tabIndex = i === 0 ? 0 : -1;

      var icone = panneau.getAttribute('data-ep-tab-icon');
      if (icone) {
        var span = document.createElement('span');
        span.className = 'ep-lt__icon';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = icone;
        bouton.appendChild(span);
      }

      var titre = panneau.querySelector('.ep-lt__h');
      var cond = panneau.querySelector('.ep-lt__cond');
      var mots = [];
      if (titre && titre.textContent.trim()) mots.push(titre.textContent.trim());
      if (cond && cond.textContent.trim()) mots.push(cond.textContent.trim());
      bouton.appendChild(document.createTextNode(mots.join(' — ')));

      // Le panneau pointe déjà sur cet identifiant : on ne le réécrit que
      // s'il visait autre chose, pour ne pas casser un lien qui tient.
      if (!panneau.getAttribute('aria-labelledby')) {
        panneau.setAttribute('aria-labelledby', bouton.id);
      }

      liste.appendChild(bouton);
    });
  }

  function initTabs(root) {
    var groups = (root || document).querySelectorAll('[data-ep-tabs]');

    Array.prototype.forEach.call(groups, function (group) {
      if (group.dataset.epTabsReady) return;

      construireOnglets(group);

      var tabs = group.querySelectorAll('[role="tab"]');
      if (tabs.length < 2) return;

      var panels = [];
      Array.prototype.forEach.call(tabs, function (tab) {
        var panel = group.querySelector('#' + CSS.escape(tab.getAttribute('aria-controls')));
        if (panel) panels.push(panel);
      });
      if (panels.length !== tabs.length) return;

      function select(index, focus) {
        Array.prototype.forEach.call(tabs, function (tab, i) {
          var on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
          panels[i].hidden = !on;
        });
        if (focus) tabs[index].focus();
      }

      Array.prototype.forEach.call(tabs, function (tab, i) {
        tab.addEventListener('click', function () { select(i, false); });
        tab.addEventListener('keydown', function (e) {
          var last = tabs.length - 1;
          var next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = last;
          if (next !== null) {
            e.preventDefault();
            select(next, true);
          }
        });
      });

      group.dataset.epTabsReady = '1';
      group.setAttribute('data-ep-tabs-ready', '');
      select(0, false);
    });
  }

  /* ── Bascule de langue ───────────────────────────────────────────────
     La boutique a deux langues. Un menu déroulant pour deux choix demande
     deux clics là où un seul suffit, et Atelier le rend en champ de
     formulaire opaque au milieu d'une barre en capitales espacées.

     On ne remplace pas le <select> : on le masque et on lui pose une
     bascule à côté. C'est lui qui porte `language_code`, donc c'est lui
     qui part avec le formulaire — on se contente de changer sa valeur.

     `form.submit()` et non `requestSubmit()` : le premier n'exécute pas
     les écouteurs de soumission, donc le gestionnaire d'Atelier ne part
     pas une seconde fois en parallèle du nôtre. La requête POST vers
     /localization reste exactement celle du thème, return_to compris.

     Rien ne se passe si le thème sort du cadre attendu — un seul choix,
     trois langues, un <select> hors formulaire. Dans ces cas l'attribut
     n'est pas posé, la feuille de style ne masque rien, et le menu natif
     reste celui que voit le visiteur.
     ─────────────────────────────────────────────────────────────────── */
  function initLangToggle(root) {
    var holders = (root || document).querySelectorAll('.language-selector');

    Array.prototype.forEach.call(holders, function (holder) {
      if (holder.hasAttribute('data-ep-lang-ready')) return;

      var select = holder.querySelector('select[name="language_code"]');
      if (!select || select.options.length !== 2) return;

      var form = select.form;
      if (!form) return;

      var group = document.createElement('div');
      group.className = 'ep-lang';
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', select.getAttribute('aria-label') || 'Language');

      Array.prototype.forEach.call(select.options, function (option, index) {
        if (index) {
          var sep = document.createElement('span');
          sep.className = 'ep-lang__sep';
          sep.setAttribute('aria-hidden', 'true');
          sep.textContent = '/';
          group.appendChild(sep);
        }

        // « fr-CA » se réduit à « FR » : c'est la langue qui est offerte,
        // pas la région — celle-ci a son propre sélecteur.
        var code = String(option.value || '').split('-')[0].toUpperCase();
        var endonym = option.textContent.trim();
        var el;

        if (option.selected) {
          // La langue courante n'est pas un bouton : cliquer dessus ne
          // ferait rien. Le lecteur d'écran l'annonce comme l'élément
          // courant du groupe, ce qui est exactement ce qu'elle est.
          el = document.createElement('span');
          el.className = 'ep-lang__on';
          el.setAttribute('aria-current', 'true');
        } else {
          el = document.createElement('button');
          el.type = 'button';
          el.className = 'ep-lang__off';
          // « FR » se lit « ef-erre » à voix haute ; on annonce l'endonyme.
          el.setAttribute('aria-label', endonym);
          el.addEventListener('click', function () {
            select.value = option.value;
            form.submit();
          });
        }

        el.textContent = code;
        group.appendChild(el);
      });

      select.insertAdjacentElement('afterend', group);
      holder.setAttribute('data-ep-lang-ready', '');
    });
  }

  /* ── Survivre aux re-rendus ──────────────────────────────────────────
     LE PROBLÈME, observé : on ajoute un article au panier, et la bascule
     de langue redevient un menu déroulant natif.

     Atelier redessine l'en-tête après une mise à jour du panier, via
     l'API de rendu de sections de Shopify. Le balisage de
     `.language-selector` est remplacé par du neuf : sans notre bascule,
     et surtout sans l'attribut [data-ep-lang-ready] — donc la feuille de
     style cesse de masquer le <select>, qui réapparaît.

     Nos deux points d'entrée ne couvraient pas ce cas. DOMContentLoaded
     ne se produit qu'une fois, et `shopify:section:load` n'existe que
     dans l'ÉDITEUR de thème : la boutique ne l'émet jamais. Mesuré avant
     et après un ajout au panier : 1 bascule → 0, le <select> repasse de
     `display: none` à `display: block`.

     On observe donc le document plutôt que de deviner quels événements
     Atelier émet. Un observateur attrape TOUS les re-rendus — panier,
     ajout rapide, tiroir, recherche — y compris ceux que le thème
     ajoutera plus tard sans nous prévenir.

     Les deux fonctions rappelées sont idempotentes : initLangToggle ne
     fait rien là où [data-ep-lang-ready] est déjà posé, et la typographie
     française ne réécrit un nœud que si le texte corrigé diffère. Le
     cycle converge donc au lieu de se rappeler lui-même sans fin.
     ─────────────────────────────────────────────────────────────────── */
  function surveillerLesReRendus() {
    if (!window.MutationObserver || !document.body) return;

    var enAttente = false;
    var ajoutes = [];

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var noeuds = mutations[i].addedNodes;
        for (var j = 0; j < noeuds.length; j++) {
          if (noeuds[j].nodeType === 1) ajoutes.push(noeuds[j]);
        }
      }
      if (!ajoutes.length || enAttente) return;
      enAttente = true;

      // Une image d'animation d'attente : le thème finit de poser son
      // balisage avant qu'on repasse dessus.
      requestAnimationFrame(function () {
        enAttente = false;
        var lot = ajoutes;
        ajoutes = [];
        initLangToggle(document);
        for (var k = 0; k < lot.length; k++) {
          if (lot[k].isConnected) initFrenchTypography(lot[k]);
        }
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    initHeroVideo(document);
    initTabs(document);
    initLangToggle(document);
    initFrenchTypography(document.body);
    surveillerLesReRendus();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // L'éditeur de thème recharge une section sans recharger la page.
  document.addEventListener('shopify:section:load', function (e) {
    initHeroVideo(e.target);
    initTabs(e.target);
    initLangToggle(e.target);
    initFrenchTypography(e.target);
  });
})();
